"use client";
import {
	faBook,
	faCircleQuestion,
	faComments,
	faHandshake,
	faInfoCircle,
	faMap,
	faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { motion } from "framer-motion";
import { useState } from "react";

const FAQPage = () => {
	const [activeQuestion, setActiveQuestion] = useState<string | null>(null);
	const faqSections = [
		{
			id: "basics",
			title: "Conceptos Básicos",
			icon: faInfoCircle,
			questions: [
				{
					id: "que_son_los_juegos_de_rol",
					question: "¿Qué son los juegos de rol?",
					answer: (
						<>
							Los juegos de rol son una forma de ocio basada en la narración colectiva. Un grupo de amigos y
							tú os sentaréis en torno a una mesa y contaréis una historia coordinada por un Director de
							Juego, o si jugáis con nosotros, un <strong>Master de Rol Profesional</strong>. Aunque existen
							infinidad de{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								sistemas de rol
							</a>
							, el más conocido es <strong>Dungeons & Dragons (D&D)</strong>. En{" "}
							<a
								href="https://www.losamosdelcalabozo.com"
								className="text-red-400 hover:text-red-300 underline"
							>
								nuestra web
							</a>{" "}
							verás cómo proponemos la trama e interpretamos a los personajes para crear{" "}
							<a
								href="https://www.losamosdelcalabozo.com/category/aventuras"
								className="text-red-400 hover:text-red-300 underline"
							>
								experiencias únicas
							</a>{" "}
							, desde la fantasía épica de{" "}
							<strong>
								{" "}
								<a
									href="https://www.losamosdelcalabozo.com/dragones-y-mazmorras"
									className="text-red-400 hover:text-red-300 underline"
								>
									Dragones y Mazmorras
								</a>{" "}
							</strong>{" "}
							hasta el terror o la{" "}
							<a
								href="https://www.losamosdelcalabozo.com/laseres-y-sentimientos"
								className="text-red-400 hover:text-red-300 underline"
							>
								ciencia ficción
							</a>{" "}
							.
						</>
					),
				},
				{
					id: "se_juegan_con_el_ordenador",
					question: "¿Se juegan con el ordenador?",
					answer: (
						<>
							Originalmente, no: los juegos de rol clásicos como <strong>Dungeons & Dragons</strong> son
							juegos sociales que se juegan en persona. Aunque el ordenador es una herramienta útil para
							gestionar manuales de{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								diferentes sistemas
							</a>
							, en{" "}
							<a
								href="https://www.losamosdelcalabozo.com"
								className="text-red-400 hover:text-red-300 underline"
							>
								Los Amos del Calabozo
							</a>{" "}
							apostamos por la inmersión presencial en Madrid. Para jugar a rol online, podéis contactar con
							nuestros amigos de{" "}
							<a href="https://territorioderol.es/" className="text-red-400 hover:text-red-300 underline">
								Territorio de Rol
							</a>
							.
						</>
					),
				},
				{
					id: "que_mas_necesito_para_jugar_a_rol",
					question: "¿Qué más necesito para jugar a rol?",
					answer: (
						<>
							Además del manual y amigos, necesitaréis lápices, papel, gomas de borrar y probablemente algunos
							dados. Los dados pueden tener diferentes caras (4, 6, 8, 10, 12 y 20), y cada juego utiliza unos
							dados u otros. Si vienes a{" "}
							<a
								href="https://www.losamosdelcalabozo.com/dragones-y-mazmorras"
								className="text-red-400 hover:text-red-300 underline"
							>
								jugar a Dungeons & Dragons (D&D)
							</a>{" "}
							o cualquier otro juego de{" "}
							<a
								href="https://www.losamosdelcalabozo.com/category/aventuras"
								className="text-red-400 hover:text-red-300 underline"
							>
								nuestro catálogo
							</a>
							, no te preocupes: ¡nosotros ponemos los mapas, las fichas de personaje y todo el material
							necesario!
						</>
					),
				},
			],
		},
		{
			id: "services",
			title: "Nuestros Servicios",
			icon: faHandshake,
			questions: [
				{
					id: "cuanto_cuesta_contratar_un_game_master",
					question: "¿Cuánto cuesta contratar un Game Master?",
					answer: (
						<>
							¡Menos de lo que te piensas! Nuestros <strong>Master de Rol Profesionales</strong> crean juegos
							adaptados a ti, ya sea una partida de{" "}
							<a
								href="https://www.losamosdelcalabozo.com/dragones-y-mazmorras"
								className="text-red-400 hover:text-red-300 underline"
							>
								Dungeons & Dragons
							</a>{" "}
							o cualquier otro de nuestros{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								sistemas disponibles
							</a>
							. El precio varía según jugadores y duración, pero puedes consultar nuestros{" "}
							<a href="/category/precios" className="text-red-400 hover:text-red-300 underline">
								paquetes de precios
							</a>{" "}
							aquí. Échale un vistazo a nuestra{" "}
							<a
								href="https://www.losamosdelcalabozo.com/"
								className="text-red-400 hover:text-red-300 underline"
							>
								página principal
							</a>{" "}
							y verás todo lo que podemos ofrecerte.
						</>
					),
				},
				{
					id: "donde_se_juega",
					question: "¿Dónde se juega?",
					answer: (
						<>
							En Madrid capital nos desplazamos a tu domicilio, o donde tú nos digas para tus sesiones de{" "}
							<strong>
								{" "}
								<a
									href="https://www.losamosdelcalabozo.com/category/aventuras"
									className="text-red-400 hover:text-red-300 underline"
								>
									D&D
								</a>{" "}
							</strong>
							y cualquier otro sistema que se te antoje. También puedes elegir una de las{" "}
							<a
								href="https://www.losamosdelcalabozo.com/category/aventuras"
								className="text-red-400 hover:text-red-300 underline"
							>
								aventuras de nuestro catálogo
							</a>{" "}
							para disfrutarla en nuestra sala privada en{" "}
							<a
								href="https://www.replayoutletcafe.com/"
								className="text-red-400 hover:text-red-300 underline"
							>
								Replay Cafe
							</a>
							, el lugar ideal para <strong>jugar al rol</strong> con cena y bebida. ¡Todo un festín de
							héroes!
							<br />
							<br />
							<span style={{ textDecorationLine: "line-through" }}>
								Para el resto del mundo, ofrecemos juegos online.
							</span>{" "}
							Por desgracia hemos dejado de jugar online 🙁 Pero quizá nuestros colegas de{" "}
							<a href="https://territorioderol.es/" className="text-red-400 hover:text-red-300 underline">
								Territorio de Rol
							</a>{" "}
							puedan ayudarte.
						</>
					),
				},
				{
					id: "podemos_alquilar_un_dm_para_una_campaña_completa_que_dure_varias_semanas",
					question: "¿Podemos alquilar un DM para una campaña completa que dure varias semanas?",
					answer: (
						<>
							¡Por supuesto! Somos expertos en campañas de larga duración. Muchos grupos confían en{" "}
							<a
								href="https://www.losamosdelcalabozo.com/master-de-rol-profesional-madrid"
								className="text-red-400 hover:text-red-300 underline"
							>
								nuestro equipo profesional
							</a>{" "}
							para dirigir crónicas de{" "}
							<strong>
								{" "}
								<a
									href="https://www.losamosdelcalabozo.com/category/aventuras"
									className="text-red-400 hover:text-red-300 underline"
								>
									Dungeons & Dragons (D&D)
								</a>{" "}
							</strong>{" "}
							que duran años, aunque también gestionamos campañas en otros{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								sistemas narrativos
							</a>
							.
						</>
					),
				},
				{
					id: "tienes_juegos_regulares_a_los_que_un_solo_jugador_puede_unirse",
					question: "¿Tienes juegos regulares a los que un solo jugador puede unirse?",
					answer: (
						<>
							¡Los tenemos! 🙂 Si no tienes grupo para{" "}
							<a
								href="https://www.losamosdelcalabozo.com/dragones-y-mazmorras"
								className="text-red-400 hover:text-red-300 underline"
							>
								jugar a Dungeons & Dragons en Madrid
							</a>
							, podemos integrarte en mesas con otros jugadores. Echa un ojo a nuestras{" "}
							<a
								href="https://www.losamosdelcalabozo.com/category/aventuras"
								className="text-red-400 hover:text-red-300 underline"
							>
								aventuras
							</a>{" "}
							o{" "}
							<a
								href="mailto:somos@losamosdelcalabozo.com"
								className="text-red-400 hover:text-red-300 underline"
							>
								escríbenos
							</a>{" "}
							para que te busquemos un hueco en una partida de{" "}
							<strong>
								{" "}
								<a
									href="https://www.losamosdelcalabozo.com/category/aventuras"
									className="text-red-400 hover:text-red-300 underline"
								>
									D&D
								</a>{" "}
							</strong>{" "}
							o cualquier otro sistema.
						</>
					),
				},
			],
		},
		{
			id: "terminology",
			title: "Terminología",
			icon: faBook,
			questions: [
				{
					id: "cual_es_la_diferencia_entre_un_game_master_y_un_dungeon_master",
					question: "¿Cuál es la diferencia entre un Game Master y un Dungeon Master?",
					answer: (
						<>
							Un <strong>Dungeon Master (DM)</strong> es el término oficial para quien dirige{" "}
							<a
								href="https://www.losamosdelcalabozo.com/dragones-y-mazmorras"
								className="text-red-400 hover:text-red-300 underline"
							>
								Dungeons & Dragons
							</a>
							. Un Game Master (GM) es el término general para cualquier otro de los{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								sistemas de rol
							</a>{" "}
							existentes. Nuestros{" "}
							<a
								href="https://www.losamosdelcalabozo.com/master-de-rol-profesional-madrid"
								className="text-red-400 hover:text-red-300 underline"
							>
								Masters de rol Profesionales
							</a>{" "}
							son expertos tanto en{" "}
							<strong>
								{" "}
								<a
									href="https://www.losamosdelcalabozo.com/category/dragones-y-mazmorras"
									className="text-red-400 hover:text-red-300 underline"
								>
									D&D
								</a>{" "}
							</strong>{" "}
							como en otros reglamentos.
						</>
					),
				},
				{
					id: "puedo_ser_un_amo_del_calabozo",
					question: "Yo soy máster, ¿puedo ser un Amo del Calabozo?",
					answer: (
						<>
							Actualmente no buscamos nuevos perfiles, pero si eres un{" "}
							<strong>Master de Rol Profesional</strong> experto en <strong>D&D</strong> o diversos{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								sistemas
							</a>
							, puedes enviarnos tu CV a{" "}
							<a
								href="mailto:quierosercomo@losamosdelcalabozo.com"
								className="text-red-400 hover:text-red-300 underline"
							>
								nuestro correo
							</a>
							. Buscamos gente que domine <strong>Dungeons & Dragons</strong> y sepa dar un gran espectáculo
							en{" "}
							<a
								href="https://www.losamosdelcalabozo.com/master-de-rol-profesional-madrid"
								className="text-red-400 hover:text-red-300 underline"
							>
								nuestro equipo
							</a>
							.
						</>
					),
				},
				{
					id: "cual_es_la_dieta_de_un_dragon_rojo_antiguo",
					question: "¿Cuál es la dieta de un dragon rojo antiguo?",
					answer:
						"Como cualquier Dragón Rojo de Dungeons & Dragons, los Antiguos son carnívoros. Aunque en nuestras partidas de D&D verás que tienen gustos variados, su predilección por la carne de aventureros está bien documentada. Si quieres enfrentarte a uno en Dragones y Mazmorras, ¡prepárate bien!",
				},
			],
		},
		{
			id: "about-us",
			title: "Sobre Nosotros",
			icon: faUsers,
			questions: [
				{
					id: "quien_ha_dibujado_las_estupendas_imagenes_de_la_web",
					question: "¿Quién ha dibujado las estupendas imágenes de la web?",
					answer: (
						<>
							Muchas de las ilustraciones épicas de <strong>D&D</strong> de la web son © de Linh Nguyen (
							<a
								href="https://www.artstation.com/linhmetal"
								className="text-red-400 hover:text-red-300 underline"
							>
								Art Station
							</a>
							). La cabecera de{" "}
							<a
								href="https://www.losamosdelcalabozo.com"
								className="text-red-400 hover:text-red-300 underline"
							>
								nuestra web principal
							</a>{" "}
							es de Diacritica (Wikipedia) bajo licencia CC BY-SA 3.0.
						</>
					),
				},
				{
					id: "condiciones_de_uso",
					question: "¿Cuáles son vuestras condiciones de uso?",
					answer: (
						<>
							Puedes encontrar toda la información legal sobre nuestras partidas de{" "}
							<strong>Dungeons & Dragons</strong> y otros{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								sistemas
							</a>{" "}
							detallada{" "}
							<a href="/aviso-legal/" className="text-red-400 hover:text-red-300 underline">
								aquí
							</a>
							.
						</>
					),
				},
				{
					id: "contacto",
					question: "¿Cómo puedo contactar con vosotros?",
					answer: (
						<>
							Para reservar tu próxima partida de{" "}
							<a
								href="https://www.losamosdelcalabozo.com/dragones-y-mazmorras"
								className="text-red-400 hover:text-red-300 underline"
							>
								Dungeons & Dragons (D&D)
							</a>{" "}
							o cualquier otro{" "}
							<a
								href="https://www.losamosdelcalabozo.com/sistemas"
								className="text-red-400 hover:text-red-300 underline"
							>
								sistema que te interese
							</a>
							, escríbenos a{" "}
							<a
								href="mailto:somos@losamosdelcalabozo.com"
								className="text-red-400 hover:text-red-300 underline"
							>
								somos@losamosdelcalabozo.com
							</a>
							. También puedes encontrar toda la información sobre nuestros servicios en nuestra{" "}
							<a
								href="https://www.losamosdelcalabozo.com"
								className="text-red-400 hover:text-red-300 underline"
							>
								página de inicio
							</a>
							.
						</>
					),
				},
			],
		},
	];

	const scrollToSection = (id: string) => {
		const element = document.getElementById(id);
		if (element) {
			element.scrollIntoView({ behavior: "smooth", block: "start" });
			setActiveQuestion(id);
		}
	};

	return (
		<main className="bg-black text-white">
			{/* Hero Section test*/}
			<section
				className="relative py-32 bg-cover bg-center"
				style={{ backgroundImage: "url('/images/collections/Precios/custom.webp')" }}
			>
				<div className="absolute inset-0 bg-black/60"></div>
				<div className="container mx-auto px-4 max-w-4xl relative z-10">
					<motion.h1
						className="font-amarante text-5xl md:text-7xl mb-6 text-center text-white"
						initial={{ opacity: 0, y: -20 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.8 }}
					>
						<span className="headerFont inline-block pb-2 border-b-2 border-red-700">
							Preguntas Frecuentes
						</span>
					</motion.h1>

					<motion.p
						className="text-xl text-gray-300 max-w-3xl mx-auto text-center mb-12"
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.3, duration: 0.6 }}
					>
						<strong className="">Seguro que tienes mil preguntas. Nosotros tenemos mil respuestas.</strong>
					</motion.p>
				</div>
			</section>

			{/* Index Section */}
			<section className="py-8 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						initial={{ opacity: 0 }}
						animate={{ opacity: 1 }}
						transition={{ delay: 0.5, duration: 0.6 }}
						className="bg-gray-800 rounded-lg p-6"
					>
						<h2 className="text-2xl font-amarante headerFont text-red-500 mb-4 text-center">
							<FontAwesomeIcon icon={faMap} className="mr-3 text-red-500" />
							Índice de Preguntas
						</h2>

						{faqSections.map((section) => (
							<div key={section.id} className="mb-6">
								<h3 className="text-xl font-amarante text-red-400 mb-3">
									<FontAwesomeIcon icon={section.icon} className="mr-2" />
									{section.title}
								</h3>
								<div className="grid grid-cols-1 md:grid-cols-2 gap-2 pl-6">
									{section.questions.map((question) => (
										<motion.button
											key={question.id}
											onClick={() => {
												setActiveQuestion(question.id);
												scrollToSection(question.id);
											}}
											className={`text-left px-3 py-2 rounded-lg transition-colors text-sm ${
												activeQuestion === question.id
													? "bg-red-700 text-white"
													: "bg-gray-700 hover:bg-red-600 text-gray-200"
											}`}
											whileHover={{ scale: 1.02 }}
											whileTap={{ scale: 0.98 }}
										>
											<FontAwesomeIcon icon={faCircleQuestion} className="mr-2 text-red-500" />
											{question.question}
										</motion.button>
									))}
								</div>
							</div>
						))}
					</motion.div>
				</div>
			</section>

			{/* Main Content Section */}
			<section className="py-16 bg-black">
				<div className="container mx-auto px-4 max-w-4xl">
					{faqSections.map((section) => (
						<motion.div
							key={section.id}
							id={section.id}
							initial={{ opacity: 0 }}
							whileInView={{ opacity: 1 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6 }}
							className="mb-16"
						>
							<h2 className="text-3xl font-amarante headerFont mb-8 border-b-2 border-red-700 pb-2 inline-block">
								<FontAwesomeIcon icon={section.icon} className="mr-3 text-red-500" />
								{section.title}
							</h2>

							<div className="space-y-6">
								{section.questions.map((item) => (
									<div
										key={item.id}
										id={item.id}
										className={`bg-gray-900 rounded-lg p-6 border-l-4 transition-all duration-300 ${
											activeQuestion === item.id
												? "border-red-500 shadow-lg shadow-red-900/20"
												: "border-red-700"
										}`}
									>
										<h3 className="text-xl font-amarante text-red-400 mb-4 flex items-start">
											<FontAwesomeIcon icon={faCircleQuestion} className="mr-3 text-red-500 mt-1" />
											<span>{item.question}</span>
										</h3>
										<div className="text-gray-300 pl-8">{item.answer}</div>
									</div>
								))}
							</div>
						</motion.div>
					))}
				</div>
			</section>

			{/* CTA Section */}
			<section className="py-12 bg-gray-900">
				<div className="container mx-auto px-4 max-w-4xl">
					<motion.div
						className="text-center flex flex-col md:flex-row md:items-center md:space-x-4"
						initial={{ opacity: 0 }}
						whileInView={{ opacity: 1 }}
						viewport={{ once: true }}
					>
						<h3 className="text-3xl font-amarante text-red-400 mb-6">¿Tienes más preguntas?</h3>
						<p className="text-gray-300 mb-8">
							Si no has encontrado la respuesta que buscas, no dudes en ponerte en contacto con nosotros.
						</p>

						<motion.a
							href={`https://api.whatsapp.com/send?phone=34642367972&text=${encodeURIComponent("Hola, tengo una pregunta sobre los juegos de rol. ¿Podéis ayudarme?")}`}
							className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors"
							whileHover={{
								scale: 1.1,
								boxShadow: "0 0 15px #f93d3d",
								backgroundColor: "white",
								color: "black",
								fontWeight: "900",
								fontStyle: "italic",
							}}
							whileTap={{ scale: 0.98 }}
							target="_blank"
							rel="noopener noreferrer"
						>
							<FontAwesomeIcon icon={faComments} className="mr-2" />
							Contactar por WhatsApp
						</motion.a>

						<motion.a
							href="mailto:somos@losamosdelcalabozo.com?subject=Queridos Amos del Calabozo"
							className="bg-gray-700 hover:bg-gray-600 text-white px-8 py-4 rounded-lg font-amarante text-xl transition-colors mt-4 md:mt-0"
							whileHover={{
								scale: 1.05,
								boxShadow: "0 0 15px #666",
							}}
							whileTap={{ scale: 0.98 }}
						>
							Enviar Email
						</motion.a>
					</motion.div>
				</div>
			</section>
		</main>
	);
};

export default FAQPage;
